import { useState } from 'react';
import { X, Download, Users, UserCheck, UserX, FileText } from 'lucide-react';
import { Client } from '../../types/index';
import { useClientExport, ExportType } from '../../services/hooks/useClientExport';
import { toast } from 'react-toastify';

interface ExportClientModalProps {
    clients: Client[];
    onClose: () => void;
}

const ExportClientModal: React.FC<ExportClientModalProps> = ({ clients, onClose }) => {
    const [selectedType, setSelectedType] = useState<ExportType>('active');
    const [isExporting, setIsExporting] = useState(false);
    
    const { exportClients, getExportStats, isReady } = useClientExport(clients);

    // Obtener estadísticas para cada tipo
    const allStats = getExportStats('all');
    const activeStats = getExportStats('active');
    const inactiveStats = getExportStats('inactive');

    const handleExport = async () => {
        if (!isReady) {
            toast.error('No hay datos de clientes disponibles');
            return;
        }

        const stats = getExportStats(selectedType);
        if (stats.total === 0) {
            toast.error(`No hay clientes ${getTypeLabel(selectedType).toLowerCase()} para exportar`);
            return;
        }

        setIsExporting(true);
        
        try {
            exportClients({ 
                type: selectedType,
                filename: `clientes_${selectedType}_${new Date().toISOString().split('T')[0]}`
            });
            
            toast.success(`Se exportaron ${stats.total} clientes correctamente`);
            onClose();
        } catch (error) {
            console.error('Error al exportar:', error);
            toast.error('Error al exportar los clientes');
        } finally {
            setIsExporting(false);
        }
    };

    const getTypeLabel = (type: ExportType): string => {
        switch (type) {
            case 'all': return 'Todos';
            case 'active': return 'Activos';
            case 'inactive': return 'Inactivos';
            default: return 'Todos';
        }
    };

    const getTypeIcon = (type: ExportType) => {
        switch (type) {
            case 'all': return <Users size={20} />;
            case 'active': return <UserCheck size={20} />;
            case 'inactive': return <UserX size={20} />;
            default: return <Users size={20} />;
        }
    };

    const getTypeColor = (type: ExportType): string => {
        switch (type) {
            case 'all': return 'blue';
            case 'active': return 'green';
            case 'inactive': return 'red';
            default: return 'blue';
        }
    };

    const getStatsForType = (type: ExportType) => {
        switch (type) {
            case 'all': return allStats;
            case 'active': return activeStats;
            case 'inactive': return inactiveStats;
            default: return allStats;
        }
    };

    return (
        <div className="modal-overlay fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative modal-content bg-white rounded-lg p-6 w-full max-w-md">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                    <X size={20} />
                </button>

                <div className="flex items-center mb-6">
                    <FileText className="h-6 w-6 text-blue-500 mr-2" />
                    <h2 className="text-xl font-bold">Exportar Clientes a CSV</h2>
                </div>

                <div className="space-y-4 mb-6">
                    <p className="text-gray-600 text-sm">
                        Selecciona el tipo de clientes que deseas exportar:
                    </p>

                    {/* Opciones de exportación */}
                    <div className="space-y-3">
                        {(['all', 'active', 'inactive'] as ExportType[]).map((type) => {
                            const stats = getStatsForType(type);
                            const color = getTypeColor(type);
                            const isSelected = selectedType === type;
                            
                            return (
                                <div
                                    key={type}
                                    onClick={() => setSelectedType(type)}
                                    className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                                        isSelected 
                                            ? `border-${color}-500 bg-${color}-50` 
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className={`mr-3 ${isSelected ? `text-${color}-600` : 'text-gray-400'}`}>
                                                {getTypeIcon(type)}
                                            </div>
                                            <div>
                                                <h3 className={`font-medium ${isSelected ? `text-${color}-800` : 'text-gray-800'}`}>
                                                    {getTypeLabel(type)}
                                                </h3>
                                                <p className="text-sm text-gray-500">
                                                    {stats.total} cliente{stats.total !== 1 ? 's' : ''}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="text-right text-xs text-gray-500">
                                            {stats.withPlazas > 0 && (
                                                <div>{stats.withPlazas} con plazas</div>
                                            )}
                                            {stats.enEspera > 0 && (
                                                <div>{stats.enEspera} en espera</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Preview de la exportación */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-gray-800 mb-2">Vista previa de la exportación:</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                        <div>• <strong>{getStatsForType(selectedType).total}</strong> registros a exportar</div>
                        <div>• <strong>17</strong> columnas de datos</div>
                        <div>• Formato: <strong>CSV (UTF-8)</strong></div>
                        <div>• Incluye: datos básicos, plazas, suscripciones, estados</div>
                    </div>
                </div>

                {/* Botones de acción */}
                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                        disabled={isExporting}
                    >
                        Cancelar
                    </button>
                    
                    <button
                        onClick={handleExport}
                        disabled={isExporting || !isReady || getStatsForType(selectedType).total === 0}
                        className={`flex-1 px-4 py-2 rounded text-white flex items-center justify-center gap-2 ${
                            isExporting || !isReady || getStatsForType(selectedType).total === 0
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                    >
                        {isExporting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                Exportando...
                            </>
                        ) : (
                            <>
                                <Download size={16} />
                                Descargar CSV
                            </>
                        )}
                    </button>
                </div>

                {/* Mensaje de ayuda */}
                <div className="mt-4 text-xs text-gray-500 text-center">
                    El archivo se descargará automáticamente en tu carpeta de descargas
                </div>
            </div>
        </div>
    );
};

export default ExportClientModal;
